/**
 * Roland SH-101 - Synthétiseur monophonique légendaire (1982)
 *
 * Caractéristiques:
 * - 1 VCO (Saw, Pulse avec PWM)
 * - 2 Sub-oscillateurs (Sub-1: -1 oct, Sub-2: -2 oct)
 * - Noise generator
 * - LPF 24dB résonant
 * - 1 ADSR (filter), Gate/AR (VCA)
 * - LFO (vibrato, PWM, filter)
 * - Portamento
 * - Son punchy, parfait pour basses et leads
 */

import * as Tone from 'tone';

export class SH101 {
    constructor(options = {}) {
        this.performance = options.performance || { lowPower: false };
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this._params = {
            // VCO
            vcoSaw: true,           // Saw on/off
            vcoPulse: false,        // Pulse on/off
            vcoPulseWidth: 0.5,     // Pulse width
            vcoOctave: 0,           // -2, -1, 0, +1

            // Sub oscillators
            sub1Level: 0,           // Sub-1 (-1 octave, square)
            sub2Level: 0,           // Sub-2 (-2 octaves, pulse)

            // Noise
            noiseLevel: 0,

            // LPF
            lpfCutoff: 2000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,
            lpfKeyTrack: 0.5,

            // Filter Envelope (ADSR)
            envAttack: 0.01,
            envDecay: 0.3,
            envSustain: 0.5,
            envRelease: 0.4,

            // VCA (gate or envelope mode)
            vcaMode: 'env',         // 'gate' or 'env'

            // LFO
            lfoRate: 5,
            lfoWave: 'triangle',    // triangle, square, random
            lfoToPitch: 0,          // Vibrato
            lfoToFilter: 0,         // Filter mod
            lfoToPW: 0,             // PWM

            // Portamento
            portamento: 0,

            // Master
            volume: 0.7
        };

        this._currentNote = null;
        this._createSynth();
    }

    _createSynth() {
        // LFO
        this.lfo = new Tone.LFO({
            frequency: this._params.lfoRate,
            type: this._params.lfoWave,
            min: -1,
            max: 1
        }).start();

        // LFO modulation gains
        this.lfoPitchGain = new Tone.Gain(0);
        this.lfoFilterGain = new Tone.Gain(0);
        this.lfoPWGain = new Tone.Gain(0);

        this.lfo.connect(this.lfoPitchGain);
        this.lfo.connect(this.lfoFilterGain);
        this.lfo.connect(this.lfoPWGain);

        // VCO - Sawtooth
        this.sawOsc = new Tone.Oscillator({
            type: 'sawtooth',
            frequency: 440
        });

        // VCO - Pulse
        this.pulseOsc = new Tone.PulseOscillator({
            frequency: 440,
            width: this._params.vcoPulseWidth
        });

        // Sub-1 (square, -1 octave)
        this.sub1Osc = new Tone.Oscillator({
            type: 'square',
            frequency: 220
        });

        // Sub-2 (pulse, -2 octaves)
        this.sub2Osc = new Tone.PulseOscillator({
            frequency: 110,
            width: 0.5
        });

        // Noise
        this.noise = new Tone.Noise('white');

        // Mixer gains
        this.sawGain = new Tone.Gain(this._params.vcoSaw ? 0.5 : 0);
        this.pulseGain = new Tone.Gain(this._params.vcoPulse ? 0.5 : 0);
        this.sub1Gain = new Tone.Gain(this._params.sub1Level);
        this.sub2Gain = new Tone.Gain(this._params.sub2Level);
        this.noiseGain = new Tone.Gain(this._params.noiseLevel);

        // VCO Mixer
        this.vcoMixer = new Tone.Gain(1);

        // LPF (24dB)
        this.lpf = new Tone.Filter({
            frequency: this._params.lpfCutoff,
            type: 'lowpass',
            rolloff: -24,
            Q: this._params.lpfResonance
        });

        // Filter envelope
        this.filterEnv = new Tone.Envelope({
            attack: this._params.envAttack,
            decay: this._params.envDecay,
            sustain: this._params.envSustain,
            release: this._params.envRelease
        });

        this.filterEnvGain = new Tone.Gain(this._params.lpfEnvAmount * 4000);
        this.filterEnv.connect(this.filterEnvGain);
        this.filterEnvGain.connect(this.lpf.frequency);

        // LFO to filter
        this.lfoFilterGain.connect(this.lpf.frequency);

        // VCA Envelope
        this.vcaEnv = new Tone.AmplitudeEnvelope({
            attack: this._params.envAttack,
            decay: this._params.envDecay,
            sustain: this._params.envSustain,
            release: this._params.envRelease
        });

        // Output
        this.outputGain = new Tone.Gain(this._params.volume);

        // Audio chain
        this.sawOsc.connect(this.sawGain);
        this.pulseOsc.connect(this.pulseGain);
        this.sub1Osc.connect(this.sub1Gain);
        this.sub2Osc.connect(this.sub2Gain);
        this.noise.connect(this.noiseGain);

        this.sawGain.connect(this.vcoMixer);
        this.pulseGain.connect(this.vcoMixer);
        this.sub1Gain.connect(this.vcoMixer);
        this.sub2Gain.connect(this.vcoMixer);
        this.noiseGain.connect(this.vcoMixer);

        this.vcoMixer.connect(this.lpf);
        this.lpf.connect(this.vcaEnv);
        this.vcaEnv.connect(this.outputGain);

        // LFO to pitch (vibrato)
        this.lfoPitchGain.connect(this.sawOsc.frequency);
        this.lfoPitchGain.connect(this.pulseOsc.frequency);
        this.lfoPitchGain.connect(this.sub1Osc.frequency);
        this.lfoPitchGain.connect(this.sub2Osc.frequency);

        // LFO to pulse width
        this.lfoPWGain.connect(this.pulseOsc.width);

        // Start oscillators
        this._startOscillators();
    }

    _startOscillators() {
        this.sawOsc.start();
        this.pulseOsc.start();
        this.sub1Osc.start();
        this.sub2Osc.start();
        this.noise.start();
    }

    _stopOscillators() {
        this.sawOsc.stop();
        this.pulseOsc.stop();
        this.sub1Osc.stop();
        this.sub2Osc.stop();
        this.noise.stop();
    }

    _setFrequency(freq, time) {
        const portaTime = this._params.portamento;
        const now = time || Tone.now();

        const octaveMultiplier = Math.pow(2, this._params.vcoOctave);

        if (portaTime > 0) {
            this.sawOsc.frequency.rampTo(freq * octaveMultiplier, portaTime, now);
            this.pulseOsc.frequency.rampTo(freq * octaveMultiplier, portaTime, now);
            this.sub1Osc.frequency.rampTo(freq * octaveMultiplier / 2, portaTime, now);
            this.sub2Osc.frequency.rampTo(freq * octaveMultiplier / 4, portaTime, now);
        } else {
            this.sawOsc.frequency.setValueAtTime(freq * octaveMultiplier, now);
            this.pulseOsc.frequency.setValueAtTime(freq * octaveMultiplier, now);
            this.sub1Osc.frequency.setValueAtTime(freq * octaveMultiplier / 2, now);
            this.sub2Osc.frequency.setValueAtTime(freq * octaveMultiplier / 4, now);
        }
    }

    connectTo(destination) {
        this.mixerChannel = destination;
        if (this.isPowered) {
            this.outputGain.connect(destination);
        }
    }

    setPower(on) {
        this.isPowered = on;
        if (this.mixerChannel) {
            if (on) {
                this.outputGain.connect(this.mixerChannel);
                if (this.performance.lowPower) {
                    this._startOscillators();
                }
            } else {
                this.outputGain.disconnect();
                if (this.performance.lowPower) {
                    this._stopOscillators();
                }
            }
        }
    }

    getPower() {
        return this.isPowered;
    }

    triggerAttack(note, time, velocity = 1) {
        if (!this.isPowered) return;

        const freq = Tone.Frequency(note).toFrequency();
        this._setFrequency(freq, time);
        this._currentNote = note;

        this.filterEnv.triggerAttack(time);
        this.vcaEnv.triggerAttack(time);
    }

    triggerRelease(note, time) {
        if (!this.isPowered) return;

        this.filterEnv.triggerRelease(time);
        this.vcaEnv.triggerRelease(time);
        this._currentNote = null;
    }

    triggerAttackRelease(note, duration, time, velocity = 1) {
        if (!this.isPowered) return;

        const freq = Tone.Frequency(note).toFrequency();
        const now = time || Tone.now();

        this._setFrequency(freq, now);
        this._currentNote = note;

        this.filterEnv.triggerAttackRelease(duration, now);
        this.vcaEnv.triggerAttackRelease(duration, now);
    }

    // === Setters ===

    setVcoSaw(on) {
        this._params.vcoSaw = on;
        this.sawGain.gain.value = on ? 0.5 : 0;
    }

    setVcoPulse(on) {
        this._params.vcoPulse = on;
        this.pulseGain.gain.value = on ? 0.5 : 0;
    }

    setVcoPulseWidth(width) {
        this._params.vcoPulseWidth = width;
        this.pulseOsc.width.value = width;
    }

    setVcoOctave(oct) {
        this._params.vcoOctave = oct;
        // Update frequency if a note is playing
        if (this._currentNote) {
            const freq = Tone.Frequency(this._currentNote).toFrequency();
            this._setFrequency(freq);
        }
    }

    setSub1Level(level) {
        this._params.sub1Level = level;
        this.sub1Gain.gain.value = level;
    }

    setSub2Level(level) {
        this._params.sub2Level = level;
        this.sub2Gain.gain.value = level;
    }

    setNoiseLevel(level) {
        this._params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    setLpfCutoff(freq) {
        this._params.lpfCutoff = freq;
        this.lpf.frequency.value = freq;
    }

    setLpfResonance(q) {
        this._params.lpfResonance = q;
        this.lpf.Q.value = q;
    }

    setLpfEnvAmount(amount) {
        this._params.lpfEnvAmount = amount;
        this.filterEnvGain.gain.value = amount * 4000;
    }

    setLpfKeyTrack(amount) {
        this._params.lpfKeyTrack = amount;
    }

    setEnvAttack(time) {
        this._params.envAttack = time;
        this.filterEnv.attack = time;
        this.vcaEnv.attack = time;
    }

    setEnvDecay(time) {
        this._params.envDecay = time;
        this.filterEnv.decay = time;
        this.vcaEnv.decay = time;
    }

    setEnvSustain(level) {
        this._params.envSustain = level;
        this.filterEnv.sustain = level;
        this.vcaEnv.sustain = level;
    }

    setEnvRelease(time) {
        this._params.envRelease = time;
        this.filterEnv.release = time;
        this.vcaEnv.release = time;
    }

    setVcaMode(mode) {
        this._params.vcaMode = mode;
    }

    setLfoRate(rate) {
        this._params.lfoRate = rate;
        this.lfo.frequency.value = rate;
    }

    setLfoWave(wave) {
        this._params.lfoWave = wave;
        this.lfo.type = wave;
    }

    setLfoToPitch(amount) {
        this._params.lfoToPitch = amount;
        this.lfoPitchGain.gain.value = amount * 50;
    }

    setLfoToFilter(amount) {
        this._params.lfoToFilter = amount;
        this.lfoFilterGain.gain.value = amount * 1500;
    }

    setLfoToPW(amount) {
        this._params.lfoToPW = amount;
        this.lfoPWGain.gain.value = amount * 0.4;
    }

    setPortamento(time) {
        this._params.portamento = time;
    }

    setVolume(vol) {
        this._params.volume = vol;
        this.outputGain.gain.value = vol;
    }

    getParams() {
        return { ...this._params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    dispose() {
        this.sawOsc.dispose();
        this.pulseOsc.dispose();
        this.sub1Osc.dispose();
        this.sub2Osc.dispose();
        this.noise.dispose();
        this.sawGain.dispose();
        this.pulseGain.dispose();
        this.sub1Gain.dispose();
        this.sub2Gain.dispose();
        this.noiseGain.dispose();
        this.vcoMixer.dispose();
        this.lpf.dispose();
        this.filterEnv.dispose();
        this.filterEnvGain.dispose();
        this.vcaEnv.dispose();
        this.outputGain.dispose();
        this.lfo.dispose();
        this.lfoPitchGain.dispose();
        this.lfoFilterGain.dispose();
        this.lfoPWGain.dispose();
    }
}
